import { CommonModule } from '@angular/common';
import type { OnInit } from '@angular/core';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  Inject,
  Input,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import type { AbstractControl } from '@angular/forms';
import { FormControl } from '@angular/forms';
import { PalantirIconComponent } from '../icon/icon.component';
import type { PalantirIcon } from '../icon/icon.type';
import { PalantirTextComponent } from '../text/text.component';

@Component({
  selector: 'palantir-input',
  templateUrl: './input.component.html',
  styleUrls: ['./input.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [CommonModule, PalantirTextComponent, PalantirIconComponent],
})
export class PalantirInputComponent implements OnInit {
  @Input() public label?: string;

  @Input() public control?: AbstractControl<any, any> | null;

  @Input() public prefix?: PalantirIcon;

  @Input() public suffix?: PalantirIcon;

  public inputLength = signal<number>(0);

  public maxLength = signal<number>(0);

  public isRequired = signal<boolean>(false);

  public constructor(@Inject(DestroyRef) private readonly destroyRef: DestroyRef) {}

  public ngOnInit(): void {
    this.maxLength.set(this.getMaxLengthFromValidator(this.control) ?? 0);
    this.isRequired.set(this.getRequiredValidator(this.control));

    this.handleValueChanges(this.control?.value);

    this.control?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((value) => {
      if (this.control?.untouched) {
        this.control.markAsTouched();
      }

      this.handleValueChanges(value);
      this.maxLength.set(this.getMaxLengthFromValidator(this.control) ?? 0);
      this.isRequired.set(this.getRequiredValidator(this.control));
    });
  }

  private handleValueChanges(value: any): void {
    this.inputLength.set(value?.length ?? 0);
  }

  private getMaxLengthFromValidator(
    control?: AbstractControl<any, any> | null,
    fallback?: number,
  ): number | undefined {
    const validatorFn = control?.validator;

    if (!validatorFn) {
      return fallback;
    }

    const errors = validatorFn(new FormControl({ length: Infinity }));
    return errors?.['maxlength']?.requiredLength ?? fallback;
  }

  private getRequiredValidator(control?: AbstractControl<any, any> | null): boolean {
    const validatorFn = control?.validator;

    if (!validatorFn) {
      return false;
    }

    const errors = validatorFn(new FormControl({ value: '', disabled: false }));
    return errors?.['required'] ?? false;
  }
}
